/* istanbul ignore file */
import { Icon, Popper, PopperProps } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import React, {
  KeyboardEvent,
  MouseEvent,
  FocusEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'

import useClickOutside from '@hooks/useClickOutside'

import '@ui/InteractiveTooltip/index.scss'

const HIDE_DELAY_MS = 50

type PopperInheritedProps = Pick<
  PopperProps,
  'position' | 'anchorWidth' | 'fullWidth' | 'maxWidth' | 'maxHeight' | 'opened'
>

interface InteractiveTooltipProps extends PopperInheritedProps {
  content: ReactNode
  className?: string
  popperClassName?: string
  onOpenChanged?: (isOpened: boolean) => void
  dataTag?: string
  iconSize?: 'small' | 'medium' | 'large' | 'x-large' | 'xx-large' | 'xxx-large'
  iconName?: string
  ariaLabel?: string
  disabled?: boolean
}

const InteractiveTooltip = (props: InteractiveTooltipProps): ReactElement => {
  const {
    content,
    className,
    popperClassName,
    onOpenChanged,
    dataTag,
    iconSize = 'medium',
    iconName = 'info',
    ariaLabel = '',
    disabled = false,
    opened,
    position,
    anchorWidth,
    fullWidth,
    maxWidth,
    maxHeight,
  } = props

  const [isShown, setIsShown] = useState<boolean>(false)
  const isPopupVisible = opened ?? isShown

  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null)

  const hideTimeout = useRef<ReturnType<typeof setTimeout>>()
  const tooltipId = useRef(`accessible-tooltip-${Math.random().toString(36).substring(2, 9)}`)

  const handleShownChanged = useCallback(
    (isOpened: boolean): void => {
      if (disabled || !content) return
      clearTimeout(hideTimeout.current)

      if (isOpened) {
        setIsShown(isOpened)
        onOpenChanged?.(isOpened)
      } else {
        hideTimeout.current = setTimeout(() => {
          setIsShown(isOpened)
          onOpenChanged?.(isOpened)
        }, HIDE_DELAY_MS)
      }
    },
    [disabled, onOpenChanged, content],
  )

  const elements = useMemo(
    () => [{ current: anchorElement }, { current: popperElement }],
    [anchorElement, popperElement],
  )

  const hidePopper = useCallback(() => handleShownChanged(false), [handleShownChanged])
  useClickOutside(elements, hidePopper)

  const openTooltip = (): void => handleShownChanged(true)
  const closeTooltip = (): void => handleShownChanged(false)

  const handleMouseEnter = (): void => {
    openTooltip()
  }

  const handleMouseLeave = (event: MouseEvent<HTMLButtonElement>): void => {
    if (event.relatedTarget === anchorElement || event.relatedTarget === popperElement) return
    closeTooltip()
  }

  const handleFocus = (): void => {
    openTooltip()
  }

  const handleBlur = (event: FocusEvent<HTMLButtonElement>): void => {
    if (event.relatedTarget === anchorElement || event.relatedTarget === popperElement) return
    closeTooltip()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (event.key === 'Escape') {
      closeTooltip()
      anchorElement?.blur()
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (isPopupVisible) {
        closeTooltip()
      } else {
        openTooltip()
      }
    }
  }

  return (
    <>
      <button
        ref={setAnchorElement}
        type="button"
        className={cn('ui-accessible-tooltip', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        aria-describedby={isPopupVisible ? tooltipId.current : undefined}
        aria-expanded={isPopupVisible}
        aria-label={ariaLabel || 'More information'}
        data-tag={dataTag}
        disabled={disabled}
      >
        <Icon size={iconSize} name={iconName} aria-hidden="true" />
      </button>
      {content && (
        <Popper
          opened={isPopupVisible && !disabled}
          position={position}
          className={cn('ui-accessible-tooltip__popper', popperClassName)}
          anchorWidth={anchorWidth}
          onRendered={setPopperElement}
          anchorElement={anchorElement}
          fullWidth={fullWidth}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          role="tooltip"
          id={tooltipId.current}
        >
          {content}
        </Popper>
      )}
    </>
  )
}

export default InteractiveTooltip
